import { Search as SearchIcon } from "@mui/icons-material";
import Logger from "../../../services/Logger";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import React, { useState } from "react";
import DictionaryApiService from "./DictionaryApiService";

const PiDicApiMain = () => {
    const [word, setWord] = useState("");
    const [definition, setDefinition] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchDefinition = async () => {
        if (!word.trim()) {
            setError("Please enter a word to search");
            return;
        }

        setLoading(true);
        setError(null);
        setDefinition(null);

        try {
            const result = await DictionaryApiService.getWordDefinition(word.trim(), 'english');
            setDefinition(result);
        } catch (err) {
            Logger.error("Dictionary API Error:", err);
            setError(`Failed to fetch definition: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            fetchDefinition();
        }
    };

    const clearSearch = () => {
        setWord("");
        setDefinition(null);
        setError(null);
    };

    const renderDefinition = (def) => {
        if (!def || !Array.isArray(def) || def.length === 0) {
            return <Typography variant="body2" color="text.secondary">No definition found</Typography>;
        }

        return def.map((entry, index) => (
            <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: "bold", mb: 2 }}>
                        {entry.word}
                    </Typography>

                    {entry.meanings && entry.meanings.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Meanings:
                            </Typography>
                            {entry.meanings.map((meaning, mIndex) => (
                                <Box key={mIndex} sx={{ mb: 2, ml: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                                        {meaning.partOfSpeech}
                                    </Typography>

                                    {meaning.definitions && meaning.definitions.length > 0 && (
                                        <Box sx={{ ml: 2 }}>
                                            {meaning.definitions.map((def, dIndex) => (
                                                <Box key={dIndex} sx={{ mb: 1 }}>
                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                        {def.definition}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                </CardContent>
            </Card>
        ));
    };

    return (
        <Box sx={{ maxWidth: 'lg', mx: 'auto', mt: 4, mb: 4, px: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Pi Dictionary API
            </Typography>

            <Grid container spacing={3}>
                {/* Search Section */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Word Definition Search
                        </Typography>

                        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                            <TextField
                                fullWidth
                                label="Enter a word"
                                value={word}
                                onChange={(e) => setWord(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={loading}
                                placeholder="Type a word and press Enter or click Search"
                            />
                            <Button
                                variant="contained"
                                onClick={fetchDefinition}
                                disabled={loading || !word.trim()}
                                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                                sx={{ minWidth: 120 }}
                            >
                                {loading ? "Searching..." : "Search"}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={clearSearch}
                                disabled={loading}
                            >
                                Clear
                            </Button>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Definition Results */}
                        {definition && (
                            <Card sx={{ mt: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Definition Results
                                    </Typography>
                                    {renderDefinition(definition)}
                                </CardContent>
                            </Card>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PiDicApiMain;
